import { useState, type FormEvent, type ReactNode } from 'react';

import { COPILOT_COMPOSER_ANCHOR } from '@/app/hints';
import { cn } from '@/lib/utils';
import { useHintAnchor } from '@/shared/ui/primitives';

import { ComposerActionRow } from './ComposerActionRow';
import { ComposerTextArea } from './ComposerTextArea';
import { SendButton } from './SendButton';

export interface MessageComposerProps {
  /** Called when a message is submitted */
  onSend?: (message: string) => void;
  /** Called when user stops an in-flight generation */
  onStop?: () => void;
  /** Optional left-side actions (chips) rendered above the send button row. */
  leftActions?: ReactNode;
  /** Placeholder text for the textarea */
  placeholder?: string;
  /** Whether a request/stream is in-flight (submit or streaming). */
  isBusy?: boolean;
  /** Additional class names for the container */
  className?: string;
}

/**
 * MessageComposer — the main input area for the copilot panel.
 *
 * Vertical stack with:
 * 1. Auto-growing textarea for message input
 * 2. Action row with chips (left) and send button (right)
 *
 * @remarks
 * This component manages local draft state. For persistence or
 * cross-component state, lift state up or use a store.
 *
 * @example
 * ```tsx
 * <MessageComposer
 *   onSend={(msg) => sendMessage(msg)}
 *   onFollowUpsClick={() => openFollowUps()}
 *   placeholder="Ask the copilot anything..."
 * />
 * ```
 */
export function MessageComposer({
  onSend,
  onStop,
  leftActions,
  placeholder = 'Ask Cortex',
  isBusy = false,
  className,
}: MessageComposerProps) {
  const [draft, setDraft] = useState('');
  const composerAnchorRef = useHintAnchor(COPILOT_COMPOSER_ANCHOR);

  const trimmedDraft = draft.trim();
  const canSend = trimmedDraft.length > 0;

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSend) return;

    if (isBusy) {
      onStop?.();
    }
    onSend?.(trimmedDraft);
    setDraft('');
  };

  return (
    <form
      ref={composerAnchorRef}
      onSubmit={handleSubmit}
      className={cn(
        'flex flex-col gap-2 rounded-[18px] border border-border bg-surface-sunken p-2',
        className
      )}
    >
      {/* Textarea */}
      <ComposerTextArea
        value={draft}
        onChange={setDraft}
        onSubmit={handleSubmit}
        placeholder={placeholder}
        disabled={false}
      />

      {/* Action row */}
      <ComposerActionRow
        leftSlot={leftActions}
        rightSlot={
          <SendButton
            mode={isBusy ? 'stop' : 'send'}
            disabled={isBusy ? !onStop : !canSend}
            onClick={
              isBusy
                ? () => {
                    onStop?.();
                  }
                : undefined
            }
          />
        }
      />
    </form>
  );
}
